import { zodResolver } from '@hookform/resolvers/zod';
import { Turnstile } from '@marsidev/react-turnstile';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { api } from '../api.js';
import { signUp } from '../auth/client.js';

const Schema = z.object({
  email: z.string().email(),
  password: z.string().min(12),
  name: z.string().min(1),
});
type FormData = z.infer<typeof Schema>;

export function Signup() {
  const { t } = useTranslation('auth');
  const { t: te } = useTranslation('errors');
  const [params] = useSearchParams();
  const inviteToken = params.get('invite');
  const nav = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<FormData>({ resolver: zodResolver(Schema) });
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Prefill email from invite if present
  const inviteEmail = params.get('email');
  if (inviteEmail && !errors.email) {
    setValue('email', inviteEmail);
  }

  const onSubmit = handleSubmit(async (data) => {
    setServerError(null);
    if (!captcha) {
      setServerError(te('captcha_failed'));
      return;
    }
    try {
      await signUp.email({
        email: data.email,
        password: data.password,
        name: data.name,
        // After email verification, send the user back to the WEB app (not the
        // API domain). Without this, Better Auth uses BETTER_AUTH_URL which
        // points to api.* and lands at a 404.
        callbackURL: `${window.location.origin}/login?verified=1`,
        fetchOptions: { headers: { 'cf-turnstile-response': captcha } },
      });
      // If signup includes an invite token, attempt to accept it now (will succeed if email matches)
      if (inviteToken) {
        try {
          await api(`/invitations/${inviteToken}/accept`, { method: 'POST' });
        } catch {
          // best-effort; user can still accept manually after verification
        }
      }
      setSuccess(true);
    } catch (e: unknown) {
      const err = e as { error?: string; message?: string };
      setServerError(te(err.error ?? 'internal_server_error', { defaultValue: err.message }));
    }
  });

  // Suppress unused var warning for nav (kept for future post-signup redirect logic)
  void nav;

  if (success) {
    return (
      <div className="mx-auto mt-16 max-w-md rounded border bg-white p-6">
        <h1 className="mb-2 text-xl font-semibold">{t('verifyEmail')}</h1>
        <p className="text-sm text-slate-700">
          Revisa tu correo y haz clic en el enlace de verificación para continuar.
        </p>
        <Link className="mt-4 inline-block text-sm text-blue-600" to="/login">
          {t('login')}
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-16 max-w-sm rounded border bg-white p-6">
      <h1 className="mb-4 text-xl font-semibold">{t('signup')}</h1>
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block">
          <span className="text-sm">{t('name')}</span>
          <input
            type="text"
            {...register('name')}
            className="mt-1 w-full rounded border px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="text-sm">{t('email')}</span>
          <input
            type="email"
            {...register('email')}
            readOnly={!!params.get('email')}
            className="mt-1 w-full rounded border px-3 py-2"
          />
          {errors.email && <span className="text-xs text-red-600">{errors.email.message}</span>}
        </label>
        <label className="block">
          <span className="text-sm">{t('password')}</span>
          <input
            type="password"
            {...register('password')}
            className="mt-1 w-full rounded border px-3 py-2"
          />
          {errors.password && (
            <span className="text-xs text-red-600">{errors.password.message}</span>
          )}
        </label>
        <Turnstile siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY ?? ''} onSuccess={setCaptcha} />
        {serverError && <p className="text-sm text-red-600">{serverError}</p>}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded bg-blue-600 py-2 text-white disabled:opacity-50"
        >
          {t('signup')}
        </button>
      </form>
      <div className="mt-3 text-sm">
        <Link to="/login">{t('haveAccount')}</Link>
      </div>
    </div>
  );
}
